/**
 * Playback of the recorder's own file, before the pipeline has cut a version
 * for the browser.
 *
 * The Mac recorder writes an append-only fragmented MP4: an initialization
 * segment (ftyp + moov) and then a moof + mdat pair every couple of seconds,
 * uploaded while the take is still going. Nothing is written when it stops,
 * so the header says the movie is zero seconds long and there is no index of
 * the fragments (no sidx, no mfra). A browser handed that file as a plain
 * `src` cannot tell how long it is or where anything sits, and Chrome pulls
 * the whole file down looking before it shows a frame: for a few minutes of
 * 1080p that is a minute of a player that looks ready and does nothing.
 *
 * Media Source Extensions want exactly the shape the recorder wrote. The
 * initialization segment goes in first, then each moof + mdat pair, each of
 * which carries its own timestamp; the first seconds play as soon as they
 * have arrived and the download keeps a couple of minutes ahead of the
 * playhead. A seek past what has arrived reads on without appending until
 * the target comes into view; a seek back into what was thrown away starts a
 * ranged download again from the fragment before the target, whose offset
 * was noted the first time past.
 *
 * Anything that is not that file (a WebM from the browser recorder, an older
 * MP4 with its header at the end, a codec the browser cannot buffer, a
 * browser without MSE) is handed straight to the element as a `src`, which
 * is what happened before.
 */

export interface FragmentedSourceOptions {
  /** How long the take is, from the recorder; the file itself does not say. */
  durationSec: number | null;
  onError: (message: string) => void;
}

/** Seconds kept ready ahead of the playhead before the download waits. */
const AHEAD_SEC = 90;
/** Seconds kept behind the playhead when the buffer has to make room. */
const BEHIND_SEC = 30;

const UNPLAYABLE = 'This video would not play. Try reloading the page.';
const UNREACHABLE = 'The recording could not be loaded. Check your connection and try again.';

function boxType(bytes: Uint8Array, offset: number): string {
  return String.fromCharCode(bytes[offset + 4], bytes[offset + 5], bytes[offset + 6], bytes[offset + 7]);
}

/** The box size at `offset`; NaN when the bytes for a 64-bit size are not in yet. */
function boxSize(bytes: Uint8Array, offset: number, end: number): number {
  const view = new DataView(bytes.buffer, bytes.byteOffset);
  const size = view.getUint32(offset);
  if (size === 1) return end - offset >= 16 ? Number(view.getBigUint64(offset + 8)) : NaN;
  // Zero means "to the end of the file", which for a stream is "everything
  // there ever is". Treated as never complete, and it is never the box type
  // that matters here.
  return size === 0 ? Infinity : size;
}

function concat(parts: Uint8Array[]): Uint8Array {
  if (parts.length === 1) return parts[0];
  const out = new Uint8Array(parts.reduce((n, p) => n + p.byteLength, 0));
  let at = 0;
  for (const part of parts) {
    out.set(part, at);
    at += part.byteLength;
  }
  return out;
}

/** The child boxes of a container: each as [type, start of its header, end]. */
function children(bytes: Uint8Array, start: number, end: number): Array<[string, number, number]> {
  const out: Array<[string, number, number]> = [];
  let at = start;
  while (at + 8 <= end) {
    const size = boxSize(bytes, at, end);
    if (!Number.isFinite(size) || size < 8 || at + size > end) break;
    out.push([boxType(bytes, at), at, at + size]);
    at += size;
  }
  return out;
}

function child(bytes: Uint8Array, start: number, end: number, type: string): [number, number] | null {
  for (const [kind, from, to] of children(bytes, start, end)) {
    if (kind === type) return [from, to];
  }
  return null;
}

function hex2(value: number): string {
  return value.toString(16).padStart(2, '0');
}

interface Track {
  id: number;
  timescale: number;
  /** The RFC 6381 codec string for `codecs=`, or null when it could not be read. */
  codec: string | null;
}

/**
 * The audio object type out of an esds box: a chain of tagged descriptors
 * with a variable-length size after each tag.
 */
function aacCodec(bytes: Uint8Array, start: number, end: number): string {
  let at = start + 4; // full box: version and flags
  const descriptor = () => {
    const tag = bytes[at];
    at += 1;
    let size = 0;
    for (let n = 0; n < 4 && at < end; n += 1) {
      const byte = bytes[at];
      at += 1;
      size = (size << 7) | (byte & 0x7f);
      if ((byte & 0x80) === 0) break;
    }
    return { tag, size };
  };
  if (at >= end) return 'mp4a.40.2';
  const es = descriptor();
  if (es.tag !== 0x03) return 'mp4a.40.2';
  const flags = bytes[at + 2];
  at += 3;
  if (flags & 0x80) at += 2;
  if (flags & 0x40) at += 1 + bytes[at];
  if (flags & 0x20) at += 2;
  const config = descriptor();
  if (config.tag !== 0x04) return 'mp4a.40.2';
  const objectType = bytes[at];
  at += 13;
  const specific = descriptor();
  if (specific.tag !== 0x05 || at >= end) return `mp4a.${hex2(objectType)}.2`;
  let audioObjectType = bytes[at] >> 3;
  if (audioObjectType === 31) audioObjectType = 32 + (((bytes[at] & 0x07) << 3) | (bytes[at + 1] >> 5));
  return `mp4a.${hex2(objectType)}.${audioObjectType}`;
}

/** The codec string for the first sample entry of a stsd box. */
function codecOf(bytes: Uint8Array, stsdStart: number, stsdEnd: number): string | null {
  const entry = stsdStart + 16; // header, version and flags, entry count
  if (entry + 8 > stsdEnd) return null;
  const type = boxType(bytes, entry);
  const entryEnd = Math.min(stsdEnd, entry + boxSize(bytes, entry, stsdEnd));
  if (type === 'avc1' || type === 'avc3') {
    // A visual sample entry is 78 bytes of fixed fields before its children.
    const avcC = child(bytes, entry + 8 + 78, entryEnd, 'avcC');
    if (!avcC) return type;
    const body = avcC[0] + 8;
    return `${type}.${hex2(bytes[body + 1])}${hex2(bytes[body + 2])}${hex2(bytes[body + 3])}`;
  }
  if (type === 'mp4a') {
    // An audio sample entry is 28 bytes of fixed fields, more in the
    // QuickTime versions that carry extra sample description fields.
    const version = (bytes[entry + 16] << 8) | bytes[entry + 17];
    const fixed = 28 + (version === 1 ? 16 : version === 2 ? 36 : 0);
    const esds = child(bytes, entry + 8 + fixed, entryEnd, 'esds');
    return esds ? aacCodec(bytes, esds[0] + 8, esds[1]) : 'mp4a.40.2';
  }
  if (type === 'Opus') return 'opus';
  if (type === 'fLaC') return 'flac';
  // Something this reader does not know how to describe. The type alone is
  // handed to isTypeSupported, which says no for anything that needs more.
  return type;
}

function parseMoov(moov: Uint8Array): Track[] {
  const tracks: Track[] = [];
  for (const [type, start, end] of children(moov, 8, moov.byteLength)) {
    if (type !== 'trak') continue;
    const tkhd = child(moov, start + 8, end, 'tkhd');
    const mdia = child(moov, start + 8, end, 'mdia');
    if (!tkhd || !mdia) continue;
    const view = new DataView(moov.buffer, moov.byteOffset);
    const tkhdVersion = moov[tkhd[0] + 8];
    const id = view.getUint32(tkhd[0] + 12 + (tkhdVersion === 1 ? 16 : 8));
    const mdhd = child(moov, mdia[0] + 8, mdia[1], 'mdhd');
    if (!mdhd) continue;
    const mdhdVersion = moov[mdhd[0] + 8];
    const timescale = view.getUint32(mdhd[0] + 12 + (mdhdVersion === 1 ? 16 : 8));
    const minf = child(moov, mdia[0] + 8, mdia[1], 'minf');
    const stbl = minf ? child(moov, minf[0] + 8, minf[1], 'stbl') : null;
    const stsd = stbl ? child(moov, stbl[0] + 8, stbl[1], 'stsd') : null;
    tracks.push({ id, timescale, codec: stsd ? codecOf(moov, stsd[0], stsd[1]) : null });
  }
  return tracks;
}

/**
 * Where a fragment starts, in seconds, from the earliest of its tracks'
 * decode times; and which tracks it carries, since a writer may put each
 * track in fragments of its own.
 */
function fragmentInfo(moof: Uint8Array, tracks: Track[]): { timeSec: number | null; tracks: string } {
  const view = new DataView(moof.buffer, moof.byteOffset);
  let earliest: number | null = null;
  const ids: number[] = [];
  for (const [type, start, end] of children(moof, 8, moof.byteLength)) {
    if (type !== 'traf') continue;
    const tfhd = child(moof, start + 8, end, 'tfhd');
    const tfdt = child(moof, start + 8, end, 'tfdt');
    if (!tfhd) continue;
    const id = view.getUint32(tfhd[0] + 12);
    ids.push(id);
    const track = tracks.find((t) => t.id === id);
    if (!tfdt || !track || track.timescale === 0) continue;
    const version = moof[tfdt[0] + 8];
    const base = version === 1 ? Number(view.getBigUint64(tfdt[0] + 12)) : view.getUint32(tfdt[0] + 12);
    const seconds = base / track.timescale;
    if (earliest === null || seconds < earliest) earliest = seconds;
  }
  return { timeSec: earliest, tracks: ids.join(',') };
}

interface Segment {
  bytes: Uint8Array;
  timeSec: number | null;
  /** The tracks in it, as a key. */
  tracks: string;
}

/**
 * Feed the file at `url` to `video` through a MediaSource. Returns the
 * function that stops the download and lets go of the element.
 */
export function attachFragmentedSource(
  video: HTMLVideoElement,
  url: string,
  options: FragmentedSourceOptions,
): () => void {
  if (typeof MediaSource === 'undefined') {
    video.src = url;
    return () => {};
  }

  const mediaSource = new MediaSource();
  const objectUrl = URL.createObjectURL(mediaSource);
  const opened = new Promise<void>((resolve) => {
    mediaSource.addEventListener('sourceopen', () => resolve(), { once: true });
  });

  let disposed = false;
  let handedOver = false;
  let sourceBuffer: SourceBuffer | null = null;
  let controller: AbortController | null = null;
  let tracks: Track[] = [];
  const head: Uint8Array[] = [];
  let init: Uint8Array | null = null;
  /** Segments waiting for the buffer to take them. */
  const queue: Uint8Array[] = [];
  /** Every fragment seen so far: where it starts in the file and on the clock. */
  const index: Array<{ offset: number; timeSec: number }> = [];
  /** The whole file has been read (from wherever the current read began). */
  let finished = false;
  /** Reading past fragments before this moment, after a seek into the unread. */
  let skipUntilSec: number | null = null;
  /**
   * The last fragment before that moment, kept back until the next one
   * shows it is the last; one per set of tracks, for a writer that puts
   * each track in fragments of its own.
   */
  const held = new Map<string, Segment>();
  /** Wakes a read that is waiting for room or for the playhead to move. */
  let wake: (() => void) | null = null;

  const fail = (message: string) => {
    if (!disposed && !handedOver) options.onError(message);
  };

  /** Give up on buffering it and hand the browser the file, as before. */
  const handOver = () => {
    if (disposed || handedOver) return;
    handedOver = true;
    controller?.abort();
    URL.revokeObjectURL(objectUrl);
    video.src = url;
  };

  const bufferedAhead = () => {
    const ranges = sourceBuffer?.buffered;
    if (!ranges) return 0;
    const at = video.currentTime;
    for (let i = 0; i < ranges.length; i += 1) {
      if (ranges.start(i) <= at + 0.5 && ranges.end(i) >= at) return ranges.end(i) - at;
    }
    return 0;
  };

  const isBuffered = (seconds: number) => {
    const ranges = sourceBuffer?.buffered;
    if (!ranges) return false;
    for (let i = 0; i < ranges.length; i += 1) {
      if (ranges.start(i) <= seconds + 0.5 && ranges.end(i) >= seconds) return true;
    }
    return false;
  };

  const waitForWake = (signal: AbortSignal) =>
    new Promise<void>((resolve) => {
      if (signal.aborted) return resolve();
      wake = resolve;
      signal.addEventListener('abort', () => resolve(), { once: true });
    });

  /** Append the next segment, if the buffer is free to take one. */
  const pump = () => {
    if (!sourceBuffer || sourceBuffer.updating || disposed || handedOver) return;
    if (queue.length === 0) {
      if (finished && held.size === 0 && mediaSource.readyState === 'open') {
        try {
          mediaSource.endOfStream();
        } catch {
          // Already ended, or an append is in flight after all.
        }
      }
      return;
    }
    try {
      sourceBuffer.appendBuffer(queue[0]);
      queue.shift();
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === 'QuotaExceededError') {
        // Full. Let go of what has played, then try again once that is
        // done; with nothing behind the playhead to let go of, wait for it
        // to move on.
        const cut = Math.max(0, video.currentTime - BEHIND_SEC);
        const ranges = sourceBuffer.buffered;
        if (ranges.length > 0 && ranges.start(0) < cut) {
          sourceBuffer.remove(0, cut);
        } else {
          video.addEventListener('timeupdate', pump, { once: true });
        }
        return;
      }
      fail(UNPLAYABLE);
    }
  };

  /** Whatever was kept back goes in, in the order the file had it. */
  const releaseHeld = () => {
    for (const segment of held.values()) queue.push(segment.bytes);
    held.clear();
    skipUntilSec = null;
  };

  const enqueue = (segment: Segment) => {
    if (skipUntilSec !== null && segment.timeSec !== null) {
      if (segment.timeSec <= skipUntilSec) {
        // Later than the one kept before it, so it takes that one's place
        // and its turn.
        held.delete(segment.tracks);
        held.set(segment.tracks, segment);
        return;
      }
      releaseHeld();
    }
    queue.push(segment.bytes);
    pump();
  };

  const openBuffer = async (moov: Uint8Array) => {
    tracks = parseMoov(moov);
    const codecs = tracks.map((t) => t.codec).filter((c): c is string => c !== null);
    const mime = `video/mp4; codecs="${codecs.join(',')}"`;
    if (codecs.length === 0 || !MediaSource.isTypeSupported(mime)) {
      handOver();
      return false;
    }
    await opened;
    if (disposed || handedOver) return false;
    try {
      sourceBuffer = mediaSource.addSourceBuffer(mime);
    } catch {
      handOver();
      return false;
    }
    sourceBuffer.addEventListener('updateend', pump);
    sourceBuffer.addEventListener('error', () => fail(UNPLAYABLE));
    if (options.durationSec !== null && Number.isFinite(options.durationSec) && options.durationSec > 0) {
      // The file says zero; the recorder knows better. The buffer will
      // stretch it if the fragments run past.
      mediaSource.duration = options.durationSec;
    }
    return true;
  };

  /** Read the file from `from`, parsing boxes as they arrive. */
  const read = async (from: number) => {
    controller?.abort();
    const own = new AbortController();
    controller = own;
    finished = false;

    let response: Response;
    try {
      response = await fetch(url, {
        signal: own.signal,
        headers: from > 0 ? { Range: `bytes=${from}-` } : undefined,
      });
    } catch {
      if (!own.signal.aborted) fail(UNREACHABLE);
      return;
    }
    if (own.signal.aborted) return;
    if (!response.ok || !response.body) {
      fail(UNREACHABLE);
      return;
    }
    // A server that ignores the range sends the file from the top; the
    // fragments before the target are then read past like any other seek
    // into the unread.
    let position = from > 0 && response.status === 206 ? from : 0;
    if (from > 0 && position === 0 && skipUntilSec === null) skipUntilSec = video.currentTime;

    const reader = response.body.getReader();
    let pending = new Uint8Array(0);
    let pendingMoof: { bytes: Uint8Array; offset: number } | null = null;

    for (;;) {
      // Enough ahead of the playhead: let the network idle until it is
      // wanted. A seek wakes this up.
      while (!own.signal.aborted && skipUntilSec === null && bufferedAhead() >= AHEAD_SEC) {
        await waitForWake(own.signal);
      }
      if (own.signal.aborted) return;

      let chunk: ReadableStreamReadResult<Uint8Array>;
      try {
        chunk = await reader.read();
      } catch {
        if (!own.signal.aborted) fail(UNREACHABLE);
        return;
      }
      if (own.signal.aborted) return;
      if (chunk.value) pending = pending.byteLength === 0 ? chunk.value : concat([pending, chunk.value]);

      let offset = 0;
      while (pending.byteLength - offset >= 8) {
        const type = boxType(pending, offset);
        // Judged from the header, before the box is in: an mdat before the
        // moov is a header-at-the-end file, and an mdat straight after the
        // moov is an ordinary MP4. Neither is the recorder's, and the
        // browser plays both on its own.
        if (!init && position === 0 && offset === 0 && type !== 'ftyp') return handOver();
        if (type === 'mdat' && (!init || !pendingMoof)) return handOver();
        const size = boxSize(pending, offset, pending.byteLength);
        if (Number.isNaN(size) || pending.byteLength - offset < size) break;
        if (size < 8) return fail(UNPLAYABLE);
        const box = pending.slice(offset, offset + size);
        const boxOffset = position + offset;
        offset += size;

        if (!init) {
          head.push(box);
          if (type === 'moov') {
            init = concat(head);
            if (!(await openBuffer(box))) return;
            if (own.signal.aborted) return;
            queue.push(init);
            pump();
          }
          continue;
        }
        if (type === 'moof') {
          pendingMoof = { bytes: box, offset: boxOffset };
        } else if (type === 'mdat' && pendingMoof) {
          const info = fragmentInfo(pendingMoof.bytes, tracks);
          const last = index[index.length - 1];
          if (info.timeSec !== null && (!last || last.offset < pendingMoof.offset)) {
            index.push({ offset: pendingMoof.offset, timeSec: info.timeSec });
          }
          enqueue({ bytes: concat([pendingMoof.bytes, box]), ...info });
          pendingMoof = null;
        }
        // Anything else (styp, sidx, free, mfra) is not needed to play.
      }
      if (offset > 0) {
        pending = pending.slice(offset);
        position += offset;
      }
      if (chunk.done) break;
    }

    // The target of a seek was in the last fragments of all.
    if (held.size > 0) releaseHeld();
    finished = true;
    pump();
  };

  const onSeeking = () => {
    if (!sourceBuffer || handedOver) return;
    const target = video.currentTime;
    if (isBuffered(target)) return;
    const last = index[index.length - 1];
    if (last && (target <= last.timeSec || finished)) {
      // Already been past it: start again from the fragment before it.
      let entry = index[0];
      for (const candidate of index) {
        if (candidate.timeSec <= target) entry = candidate;
      }
      queue.length = 0;
      held.clear();
      skipUntilSec = target;
      if (mediaSource.readyState === 'open' && sourceBuffer.updating) sourceBuffer.abort();
      void read(entry.offset);
    } else {
      // Not there yet: keep reading, but stop appending until it is.
      queue.length = 0;
      held.clear();
      skipUntilSec = target;
      wake?.();
    }
  };
  const onTime = () => wake?.();

  video.addEventListener('seeking', onSeeking);
  video.addEventListener('timeupdate', onTime);
  video.src = objectUrl;
  void read(0);

  return () => {
    disposed = true;
    controller?.abort();
    wake?.();
    video.removeEventListener('seeking', onSeeking);
    video.removeEventListener('timeupdate', onTime);
    video.removeEventListener('timeupdate', pump);
    if (!handedOver) URL.revokeObjectURL(objectUrl);
  };
}
